import { create } from 'zustand';
import type { AppUser, Agent } from '../types';
import { fetchIdentity } from './api';

interface AuthState {
  user:           AppUser | null;
  agents:         Agent[];
  trustedOrigins: string[];
  status:         'loading' | 'authenticated' | 'unauthenticated';
  setUser:        (u: AppUser) => void;
  setAgents:      (a: Agent[]) => void;
  bootstrapFromCloudflare: (idToken: string, userInfo: Partial<AppUser>) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user:           null,
  agents:         [],
  trustedOrigins: ['https://billfreetech.pages.dev', 'http://localhost:5173'],
  status:         'loading',

  setUser:   (user)   => set({ user, status: 'authenticated' }),
  setAgents: (agents) => set({ agents }),

  bootstrapFromCloudflare: async (idToken, userInfo) => {
    try {
      const res = await fetchIdentity(idToken);
      if (res.success && res.email) {
        set({
          user: {
            email:   res.email,
            name:    res.name   || userInfo.name   || res.email,
            token:   res.token  || '',
            role:    (res.role  || 'viewer') as AppUser['role'],
            isAdmin: res.isAdmin ?? false,
            picture: userInfo.picture,
          },
          agents:         (res.agents || []) as Agent[],
          trustedOrigins: res.trustedOrigins?.length
            ? res.trustedOrigins
            : get().trustedOrigins,
          status: 'authenticated',
        });
      } else {
        // ── FALLBACK: Identity endpoint returned but user not recognised.
        // In standalone/dev mode, create a demo session so the dashboard loads.
        console.warn('[Auth] Identity returned no email — using demo session');
        setDemoUser(set);
      }
    } catch (e) {
      // ── FALLBACK: Identity fetch failed (CORS, network, etc.)
      // Instead of blocking the entire app, use demo mode so the user
      // can at least see the dashboard with mock data locally.
      console.warn('[Auth] Identity fetch failed, using demo session:', e);
      setDemoUser(set);
    }
  },
}));

/** Sets a demo/dev user so the dashboard loads with mock data instead of showing Access Denied */
function setDemoUser(set: (s: Partial<AuthState>) => void) {
  set({
    user: {
      email:   'demo@billfree.in',
      name:    'Demo User',
      token:   '',
      role:    'admin',
      isAdmin: true,
    },
    status: 'authenticated',
  });
}

/**
 * Cloudflare postMessage bridge.
 * In production the app runs inside a Cloudflare Pages iframe wrapper that
 * sends BT_AUTH_SYNC after Google OAuth completes.
 * In dev (no parent) we fall back to fetchIdentity('') which uses mock data.
 *
 * Call once in App.tsx on mount. Returns cleanup function.
 */
export function initCloudflareAuthBridge(
  onAuth: (idToken: string, userInfo: Partial<AppUser>) => void
): () => void {
  const inIframe = window !== window.top;

  const handleMessage = (e: MessageEvent) => {
    const trustedOrigins = useAuthStore.getState().trustedOrigins;
    if (!trustedOrigins.some(o => e.origin.startsWith(o))) return;
    if (e.source !== window.parent) return;
    if (e.data?.type !== 'BT_AUTH_SYNC') return;

    const { email, name, picture, token } = e.data as {
      type: string; email?: string; name?: string; picture?: string; token?: string;
    };
    onAuth(token || '', { email, name, picture });
  };

  window.addEventListener('message', handleMessage);

  if (inIframe) {
    try {
      const origins = useAuthStore.getState().trustedOrigins;
      window.parent.postMessage({ type: 'BT_APP_READY' }, origins[0] || '*');
    } catch { /* cross-origin security: ignore */ }
  } else {
    // Standalone / dev: bootstrap immediately without a token
    onAuth('', {});
  }

  return () => window.removeEventListener('message', handleMessage);
}
